/**
 * Badge (shadcn/ui, adapted). The broadcast variants are the tally states a
 * rundown, a part or a device shows: `onair`, `next`, `preview`, `standby`,
 * `offline`, plus `warning` and `success`.
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/utils.js';

export const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
        onair: 'border-transparent bg-onair text-onair-foreground uppercase tracking-wide',
        next: 'border-transparent bg-next text-next-foreground uppercase tracking-wide',
        preview: 'border-transparent bg-preview text-preview-foreground uppercase tracking-wide',
        standby: 'border-transparent bg-standby text-standby-foreground',
        offline: 'border-transparent bg-offline text-offline-foreground',
        warning: 'border-transparent bg-warning text-warning-foreground',
        success: 'border-transparent bg-success text-success-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean };

export function Badge({ className, variant, asChild, ...props }: BadgeProps) {
  const Comp = asChild === true ? Slot.Root : 'span';
  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}
